import { Alert, Button, Col, Modal, Row, Tag } from 'antd';
import DOMPurify from 'dompurify';
import React, { useCallback, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import FullPageLoader from '../../../foundation/components/full_page_loader/FullPageLoader';
import {
  useGetGeneFactsOverviewQuery,
  useGetInterpretationSnapshotQuery,
  useGetVariantAssessmentArgumentsQuery,
  useGetVariantAssessmentStagesQuery,
  useGetVariantCommentsQuery,
  useGetVariantDetailQuery,
  useGetVariantHistoryQuery,
  useGetVariantInterpretationZoneQuery,
  useGetVariantLinkoutsQuery,
  useGetVariantMolecularImpactHeaderQuery,
  useGetVariantReferencesQuery,
} from '../../api/variantApi';
import CollapseHeaderForSections from '../components/CollapseHeaderForSections';
import CollapsePanel from '../components/CollapsePanel';
import DetailCard from '../components/DetailCard';
import ReferencesOverview from '../components/ReferencesOverview';
import VariantUpdatePrimaryTranscript from '../components/VariantUpdatePrimaryTranscript';
import { VARIANT_STAGE_TYPES } from '../constants';
import { useVariantStep } from '../hooks/use_variant_step';
import Interpretation from '../interpretation/Interpretation';
import type {
  InterpretationSnapshot,
  InterpretationSnapshotPayload,
} from '../redux';
import ClassificationSection from './ClassificationSection';
import GeneFactsOverviewSection from './GeneFactsOverviewSection'; // import der Komponente
import useInterpretationSnapshotLogic from './hooks/useInterpretationSnapshotLogic';
import InterpretationHeaderSection from './InterpretationHeaderSection';
import { compareInterpretationSnapshots } from './snapshotDiff';

type LiveInterpretationProps = {
  interpretationRef: React.RefObject<HTMLDivElement>;
  captureOnly?: boolean;
  registerSnapshotCapture?: boolean;
  variantId?: string;
};

type SemanticDiffModalProps = {
  currentPayload: InterpretationSnapshotPayload | null;
  snapshotPayload: InterpretationSnapshotPayload;
  open: boolean;
  onClose: () => void;
};

const formatDiffValue = (value: unknown) => {
  if (value === undefined) {
    return '—';
  }

  if (typeof value === 'string') {
    return value;
  }

  return JSON.stringify(value);
};

const SemanticDiffModal = ({
  currentPayload,
  snapshotPayload,
  open,
  onClose,
}: SemanticDiffModalProps) => {
  const diffs = currentPayload
    ? compareInterpretationSnapshots(snapshotPayload, currentPayload)
    : [];

  return (
    <Modal
      open={open}
      title="Semantic comparison with current data"
      footer={null}
      width={900}
      onCancel={onClose}
    >
      {!currentPayload ? (
        <Alert
          type="info"
          message="Current interpretation data is still loading."
        />
      ) : diffs.length === 0 ? (
        <Alert type="success" message="No semantic differences found." />
      ) : (
        <div className="interpretation-semantic-diff">
          <Alert
            type="warning"
            message={`${diffs.length} semantic difference${diffs.length === 1 ? '' : 's'} found`}
          />
          <div className="interpretation-semantic-diff__entries">
            {diffs.map((diff) => (
              <div
                className="interpretation-semantic-diff__entry"
                key={`${diff.kind}-${diff.path}`}
              >
                <Tag
                  color={
                    diff.kind === 'added'
                      ? 'green'
                      : diff.kind === 'removed'
                        ? 'red'
                        : 'gold'
                  }
                >
                  {diff.kind}
                </Tag>
                <code>{diff.path}</code>
                <div className="interpretation-semantic-diff__values">
                  {diff.kind !== 'added' && (
                    <span>Snapshot: {formatDiffValue(diff.before)}</span>
                  )}
                  {diff.kind !== 'removed' && (
                    <span>Current: {formatDiffValue(diff.after)}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
};

type InterpretationSnapshotTabProps = {
  snapshot: InterpretationSnapshot;
  currentPayload: InterpretationSnapshotPayload | null;
};

const InterpretationSnapshotTab = ({
  snapshot,
  currentPayload,
}: InterpretationSnapshotTabProps) => {
  const renderedHtml = snapshot.payload.rendered_html;
  const [isSemanticDiffOpen, setIsSemanticDiffOpen] = useState(false);

  return (
    <div className="interpretation-snapshot-tab">
      <div className="interpretation-snapshot-tab__header">
        <span>
          Snapshot from {new Date(snapshot.created).toLocaleString()} by{' '}
          {snapshot.created_by}
        </span>
        {currentPayload && (
          <Button size="small" onClick={() => setIsSemanticDiffOpen(true)}>
            Compare data
          </Button>
        )}
      </div>
      <div className="interpretation-snapshot-tab__content">
        {renderedHtml ? (
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(renderedHtml),
            }}
          />
        ) : (
          <Alert
            type="error"
            message="This snapshot does not contain a rendered Interpretation view."
          />
        )}
      </div>
      <SemanticDiffModal
        currentPayload={currentPayload}
        snapshotPayload={snapshot.payload}
        open={isSemanticDiffOpen}
        onClose={() => setIsSemanticDiffOpen(false)}
      />
    </div>
  );
};

type SnapshotPageProps = {
  variantId: string;
  snapshotId: number;
};

const SnapshotPage = ({ variantId, snapshotId }: SnapshotPageProps) => {
  const { data, isLoading, isError } = useGetInterpretationSnapshotQuery({
    variant_id: variantId,
    snapshot_id: snapshotId,
  });

  if (isLoading) {
    return <FullPageLoader />;
  }

  if (isError || !data) {
    return <Alert type="error" message="The snapshot could not be loaded." />;
  }

  return (
    <div className="interpretation-snapshot-page">
      <InterpretationSnapshotTab snapshot={data} currentPayload={null} />
    </div>
  );
};

const LiveInterpretation = ({
  interpretationRef,
  captureOnly = false,
  registerSnapshotCapture = false,
  variantId,
}: LiveInterpretationProps) => {
  // Read the selected variant straight from the RTK Query cache (populated
  // by the workflow loader) rather than a Redux selector.
  const { data: variant } = useGetVariantDetailQuery(
    { variant_id: variantId },
    { skip: !variantId },
  );

  const stageInfo = useVariantStep(VARIANT_STAGE_TYPES.INTERPRETATION);

  // Read linkouts straight from the query hook's cache rather than a selector
  const { data: linkouts, isSuccess: isLinkoutsLoaded } =
    useGetVariantLinkoutsQuery({ variant_id: variantId }, { skip: !variantId });

  // Query hooks auto-fetch when mounted and expose isLoading flags
  const {
    data: molecularImpactHeader,
    isLoading: isHeaderLoading,
    isSuccess: isHeaderLoaded,
  } = useGetVariantMolecularImpactHeaderQuery(
    { variant_id: variantId },
    { skip: !variantId },
  );
  const {
    data: comments,
    isLoading: isCommentsLoading,
    isSuccess: areCommentsLoaded,
  } = useGetVariantCommentsQuery(
    { variant_id: variantId },
    { skip: !variantId },
  );
  const {
    data: history,
    isLoading: isHistoryLoading,
    isSuccess: isHistoryLoaded,
    refetch: refetchHistory,
  } = useGetVariantHistoryQuery(
    { variant_id: variantId },
    { skip: !variantId },
  );
  const {
    data: interpretationZone,
    isLoading: isInterpretationZoneLoading,
    isSuccess: isInterpretationZoneLoaded,
  } = useGetVariantInterpretationZoneQuery(
    { variant_id: variantId },
    { skip: !variantId },
  );
  const {
    data: references,
    isLoading: isReferencesLoading,
    isSuccess: areReferencesLoaded,
  } = useGetVariantReferencesQuery(
    { variant_id: variantId },
    { skip: !variantId },
  );
  const {
    data: geneFactsOverview,
    isLoading: isGeneFactsLoading,
    isSuccess: areGeneFactsLoaded,
  } = useGetGeneFactsOverviewQuery(
    { variant_id: variantId },
    { skip: !variantId },
  );
  const {
    data: stages,
    isLoading: isStagesLoading,
    isSuccess: areStagesLoaded,
  } = useGetVariantAssessmentStagesQuery(
    { variant_id: variantId ? Number(variantId) : undefined },
    { skip: !variantId },
  );
  const {
    data: argumentsForClassification,
    isLoading: isArgumentsLoading,
    isSuccess: areArgumentsLoaded,
  } = useGetVariantAssessmentArgumentsQuery(
    { variant_id: variantId },
    { skip: !variantId },
  );

  // Aggregate isLoading flags from the individual query hooks
  // BIOFRO-80 ABS review: gene facts overview loading was missing, causing
  // the loader to hide before that section's data arrived.
  const combinedLoading =
    isHeaderLoading ||
    isCommentsLoading ||
    isHistoryLoading ||
    isInterpretationZoneLoading ||
    isReferencesLoading ||
    isGeneFactsLoading ||
    isStagesLoading ||
    isArgumentsLoading;

  const refreshHistory = useCallback(async () => {
    const refreshedHistory = await refetchHistory();
    return refreshedHistory.data;
  }, [refetchHistory]);

  const {
    isSnapshotCaptureExpanded,
    prepareSnapshot,
    handleAutomaticRevisionSnapshot,
  } = useInterpretationSnapshotLogic({
    interpretationRef,
    variant: variant ?? null,
    variantId: variantId,
    stages,
    interpretationZone,
    argumentsForClassification,
    molecularImpactHeader,
    comments,
    history,
    references,
    linkouts,
    geneFactsOverview,
    isLoading: combinedLoading,
    isLinkoutsLoaded,
    isHeaderLoaded,
    areCommentsLoaded,
    isHistoryLoaded,
    isInterpretationZoneLoaded,
    areReferencesLoaded,
    areGeneFactsLoaded,
    areStagesLoaded,
    areArgumentsLoaded,
    refreshHistory,
    stageStatus: stageInfo?.status,
    registerSnapshotCapture,
  });

  return (
    <div className="interpretation" ref={interpretationRef}>
      <VariantUpdatePrimaryTranscript />
      <Row gutter={[8, 8]} className="workflow-stage-content-container">
        <Col flex={'auto'} className="workflow-stage-left-col">
          <InterpretationHeaderSection
            forceExpanded={isSnapshotCaptureExpanded}
          />
          <ClassificationSection forceExpanded={isSnapshotCaptureExpanded} />
          <DetailCard>
            <CollapsePanel
              title={
                <CollapseHeaderForSections title={'References: overview'} />
              }
              panelKey="references-overview"
            >
              <div
                className="interpretation__intervar-link"
                style={{ marginBottom: 8 }}
              >
                {linkouts?.pubmed_url && (
                  <a
                    href={linkouts.pubmed_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="interpretation__intervar-link__anchor"
                  >
                    PubMed{' '}
                  </a>
                )}
                {linkouts?.mastermind_url && (
                  <a
                    href={linkouts.mastermind_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="interpretation__intervar-link__anchor"
                  >
                    MM{' '}
                  </a>
                )}
                {linkouts?.google_scholar_url && (
                  <a
                    href={linkouts.google_scholar_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="interpretation__intervar-link__anchor"
                  >
                    Scholar{' '}
                  </a>
                )}
              </div>
              <ReferencesOverview
                variantId={variantId || ''}
                stageType="interpretation"
              />
            </CollapsePanel>
          </DetailCard>

          <DetailCard>
            <CollapsePanel
              title={
                <CollapseHeaderForSections title={'Gene facts: overview'} />
              }
              panelKey="gene-facts-overview"
            >
              <GeneFactsOverviewSection
                forceExpanded={isSnapshotCaptureExpanded}
              />
            </CollapsePanel>
          </DetailCard>
        </Col>

        {stageInfo && variant && (
          <Interpretation
            variant={variant}
            stepLabel="Interpretation"
            stepStatus={stageInfo.status}
            stepId={stageInfo.id}
            showSnapshots={!captureOnly}
            onBeforeRevision={captureOnly ? undefined : prepareSnapshot}
            onAfterRevision={
              captureOnly ? undefined : handleAutomaticRevisionSnapshot
            }
          />
        )}
      </Row>
    </div>
  );
};

export const InterpretationSnapshotCapture = () => {
  const interpretationRef = useRef<HTMLDivElement>(null);
  const { vid } = useParams();

  return (
    <div className="interpretation-snapshot-capture-host" aria-hidden="true">
      <LiveInterpretation
        interpretationRef={interpretationRef}
        captureOnly
        registerSnapshotCapture
        variantId={vid}
      />
    </div>
  );
};

const MainInterpretation = () => {
  const { vid } = useParams();
  const [searchParams] = useSearchParams();
  const snapshotId = Number(searchParams.get('interpretation_snapshot'));
  const currentInterpretationRef = useRef<HTMLDivElement>(null);
  if (vid && Number.isInteger(snapshotId) && snapshotId > 0) {
    return <SnapshotPage variantId={vid} snapshotId={snapshotId} />;
  }

  return (
    <LiveInterpretation
      interpretationRef={currentInterpretationRef}
      variantId={vid}
    />
  );
};

export default MainInterpretation;
